import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocationAutoComplete } from '@/api/location/location';
import { CoordinatesResponseStatus } from '@/api/location/types';
import { Option } from '@/components/features/RegistrationCompositeDetail/shared/types';
import {
	AutoComplete,
	InputText,
	InputTextarea,
	OptionProps,
	ReactSelect,
	ReactSelectOptionProps,
	SelectWithLabel,
	ToolTip,
} from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useListApplicationRegistrationGroups } from '@/generated/application-registrations';
import { AccountId, GlobalListType, LocationDetailsResponse, LocationRequest } from '@/generated/data.schemas';
import { LocationType } from '@/generated/data.schemas';
import { useGetGlobalList } from '@/generated/global-lists';
import { useGetSettings } from '@/generated/settings';
import { useGetTranslations } from '@/generated/translations';
import { useDebounce } from '@/helpers';
import { SelectItem } from '@/types';
import styles from './LocationForm.module.scss';
import { MemoizedMap as Map } from './Map';

interface LocationFormProps {
	formId: 'edit-location-form' | 'register-location-form';
	isEdit?: boolean;
	handleSubmit: (data: LocationRequest) => void;
	validationErrors: Record<string, string[]> | null;
	location: LocationDetailsResponse;
	accountId: AccountId;
	className?: string;
}

const testKey = '';

export const LocationForm: React.FunctionComponent<LocationFormProps> = ({
	handleSubmit,
	validationErrors,
	location,
	accountId,
	formId,
	className,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: settings } = useGetSettings(segmentationId);

	const [type, setType] = useState<LocationType>(location.locationType || LocationType.Vessel);
	const [vesselType, setVesselType] = useState<string>(location.vesselType || '');
	const [identifier, setIdentifier] = useState<string>(location.identifier || '');
	const [description, setDescription] = useState<string>(location.description || '');
	const [address, setAddress] = useState<string>(location.address || '');
	const [latitude, setLatitude] = useState<string>(location.latitude || '');
	const [longitude, setLongitude] = useState<string>(location.longitude || '');

	const [forceClose, setForceClose] = useState<boolean>(false);
	const [forceClear, setForceClear] = useState<boolean>(false);

	const [currentGroup, setCurrentGroup] = useState(location.group || '');

	const { data: groupsResponse } = useListApplicationRegistrationGroups(segmentationId, accountId);

	const { data: vesselTypes, isLoading: isVesselTypesLoading } = useGetGlobalList(
		segmentationId,
		GlobalListType.VesselType,
		{
			SegmentId: undefined,
		},
	);

	const debouncedAddress = useDebounce(address, 1000);

	const debouncedLatitude = useDebounce(Number(latitude), 500);
	const debouncedLongitude = useDebounce(Number(longitude), 500);

	const { data: { addresses, status } = {}, isLoading } = useLocationAutoComplete({
		address: debouncedAddress,
		key: settings?.data.googleMapsApiKey || testKey,
	});

	const vesselTypesOptions = useMemo(() => {
		if (vesselTypes?.data) {
			return vesselTypes?.data.map((type) => {
				return {
					label: type,
					value: type,
				};
			});
		}
		return [];
	}, [vesselTypes]);

	const mappedTypeOptions: OptionProps[] = useMemo(() => {
		return [
			{
				displayName: translations?.data.locations.vessel || '',
				value: LocationType.Vessel,
			},
			{
				displayName: translations?.data.locations.site || '',
				value: LocationType.Site,
			},
		];
	}, [translations]);

	const modifiedAddresses = useMemo(() => {
		return !isLoading && addresses && addresses.length > 0
			? addresses.map((x) => {
					return {
						label: x.formatted_address,
						value: [x.formatted_address, x.geometry.location.lat, x.geometry.location.lng],
					};
				})
			: status === CoordinatesResponseStatus.ZERO_RESULTS
				? [
						{
							label: CoordinatesResponseStatus.ZERO_RESULTS,
							value: [null],
						},
					]
				: [];
	}, [addresses, isLoading, status]);

	const mappedApplicationGroupOptions = useMemo(() => {
		if (!groupsResponse?.data) {
			return [];
		}

		const list = groupsResponse.data.map((group) => ({
			label: group,
			value: group,
		}));

		if (currentGroup && !groupsResponse.data.includes(currentGroup)) {
			list.push({
				label: currentGroup,
				value: currentGroup,
			});
		}

		return list;
	}, [groupsResponse?.data, currentGroup]);

	const selectedGroup = useMemo(() => {
		return mappedApplicationGroupOptions.find((g) => g.value === currentGroup) || null;
	}, [mappedApplicationGroupOptions, currentGroup]);

	const onGroupChange = (option: Option): void => {
		setCurrentGroup(option.value);
	};

	const onCreateGroup = (value: string): void => {
		setCurrentGroup(value);
	};

	const modifyIdentifier = (e) => {
		setIdentifier(e.target.value);
	};

	const modifyDescription = (e) => {
		setDescription(e.target.value);
	};

	const handleTypeChange = (e): void => {
		const selectedValue = e.target.value;
		setType(selectedValue as LocationType);
	};

	const handleVesselTypeChange = (option: ReactSelectOptionProps) => {
		setVesselType(option.value);
	};

	const selectAddress = (option: SelectItem) => {
		// option.value = ['address', latitude, longitude]
		setLatitude(option.value?.[1]?.toString() || '');
		setLongitude(option.value?.[2]?.toString() || '');
	};

	const updateMarkerByDragging = (lat: string, lng: string) => {
		setLatitude(lat);
		setLongitude(lng);
		setForceClose(true);
		setAddress(`${lat},${lng}`);
	};

	const modifyAddress = (e) => {
		setAddress(e.target.value);
	};

	const modifyLatitude = (e) => {
		setLatitude(e.target.value);
		if (e.target.value !== '') {
			setForceClear(true);
			if (longitude) {
				setAddress(`${e.target.value},${longitude}`);
			}
		}
	};

	const modifyLongitude = (e) => {
		setLongitude(e.target.value);
		if (e.target.value !== '') {
			setForceClear(true);
			if (latitude) {
				setAddress(`${latitude},${e.target.value}`);
			}
		}
	};

	const submitForm = (event) => {
		event.preventDefault();
		const formData = new FormData(event.target || undefined);

		const data: LocationRequest = {
			address: String(formData.get('address') || ''),
			description: String(formData.get('description') || ''),
			identifier: String(formData.get('identifier') || ''),
			latitude: String(formData.get('latitude') || ''),
			locationType: type,
			longitude: String(formData.get('longitude') || ''),
			vesselType: String(formData.get('vesselType') || ''),
			operator: String(formData.get('operator') || ''),
			owner: String(formData.get('owner') || ''),
			shipyard: String(formData.get('shipyard') || ''),
			charterer: String(formData.get('charterer') || ''),
			group: currentGroup,
		};
		handleSubmit(data);
	};

	useEffect(() => {
		if (location.address === '' && location.latitude && location.longitude) {
			setAddress(`${location.latitude},${location.longitude}`);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === CoordinatesResponseStatus.ZERO_RESULTS && address === `${latitude},${longitude}`) {
			setAddress('');
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	useEffect(() => {
		if (addresses && address === location.address && formId === 'edit-location-form') {
			let payload: (string | undefined | null)[] = [];
			if (location.latitude && location.longitude) {
				payload = [location.latitude, location.longitude];
			} else {
				const x = addresses.find((x) => x.formatted_address === address)?.geometry.location;
				payload = [x?.lat?.toString(), x?.lng?.toString()];
			}
			setLatitude(payload[0] || '');
			setLongitude(payload[1] || '');
			setForceClose(true);
			window.setTimeout(() => {
				setForceClose(false);
			}, 5);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addresses]);

	useEffect(() => {
		if (forceClear) {
			window.setTimeout(() => {
				setForceClear(false);
			}, 1000);
		}
	}, [forceClear]);

	return (
		<>
			<form id={formId} onSubmit={submitForm} className={className}>
				<fieldset className={styles.fieldset}>
					<legend className={'u-hide-visually'}>{translations?.data.locations.locationName}</legend>
					<SelectWithLabel
						id="type"
						name={'type'}
						ariaLabel={translations?.data.locations.type || 'type'}
						options={mappedTypeOptions}
						value={type}
						onChange={handleTypeChange}
						variant={'inverse'}
					/>
					{type === LocationType.Vessel && !isVesselTypesLoading && (
						<ReactSelect
							ariaLabel={translations?.data.locations.vesselType || 'Vessel Type'}
							id="vesselType"
							name="vesselType"
							options={vesselTypesOptions}
							onChange={handleVesselTypeChange}
							currentValue={vesselTypesOptions.find((x) => x.value === vesselType)}
						/>
					)}

					<ReactSelect
						id="group"
						label={
							<div className={styles.groupLabel}>
								{translations?.data.applications.group || 'Group'}

								{translations?.data.applications.groupInfo && (
									<ToolTip
										className={styles.groupTooltip}
										text={translations.data.applications.groupInfo}
										label={`${translations.data.shared.moreInformationAbout} ${translations.data.applications.group}`}
									/>
								)}
							</div>
						}
						ariaLabel={translations?.data.applications.group || 'Group'}
						options={mappedApplicationGroupOptions}
						currentValue={selectedGroup}
						onChange={onGroupChange}
						handleCreate={onCreateGroup}
						placeholder={translations?.data.applications.chooseOrAddGroup}
						errorText={validationErrors?.Group?.[0]}
						variant={'inverse'}
						creatable
						isSmall
					/>
					<InputText
						name="identifier"
						id="identifier"
						label={
							type === LocationType.Vessel
								? translations?.data.locations.iMOnumber
								: translations?.data.locations.identifier
						}
						value={identifier}
						onChange={modifyIdentifier}
						variant={'inverse'}
						errorText={identifier === '' ? validationErrors?.Identifier?.[0] : undefined}
					/>
					{type === LocationType.Vessel && (
						<>
							<InputText
								name="operator"
								id="operator"
								defaultValue={location.operator || ''}
								label={translations?.data.locations.technicalManager}
								errorText={validationErrors?.Operator?.[0]}
								variant={'inverse'}
							/>
							<InputText
								name="charterer"
								id="charterer"
								defaultValue={location.charterer || ''}
								label={translations?.data.locations.charterer}
								errorText={validationErrors?.Charterer?.[0]}
								variant={'inverse'}
							/>
						</>
					)}
					<InputText
						name="owner"
						id="owner"
						defaultValue={location.owner || ''}
						label={
							type === LocationType.Site
								? translations?.data.locations.ownerOperator
								: translations?.data.locations.shipOwner
						}
						errorText={validationErrors?.Owner?.[0]}
						variant={'inverse'}
					/>
					{type === LocationType.Vessel && (
						<InputText
							name="shipyard"
							id="shipyard"
							defaultValue={location.shipyard || ''}
							label={translations?.data.locations.shipyard}
							errorText={validationErrors?.Shipyard?.[0]}
							variant={'inverse'}
						/>
					)}
					{type === LocationType.Site && (
						<>
							<AutoComplete
								onChange={modifyAddress}
								onSelect={selectAddress}
								options={modifiedAddresses}
								name={'address'}
								id={'address'}
								label={translations?.data.locations.address || 'address'}
								value={address}
								variant={'inverse'}
								forceClose={forceClose}
								forceClear={forceClear}
							/>
							<InputText
								name="latitude"
								id="latitude"
								value={latitude}
								label={translations?.data.locations.latitude}
								errorText={latitude === '' ? validationErrors?.Latitude?.[0] : undefined}
								variant={'inverse'}
								onChange={modifyLatitude}
							/>
							<InputText
								name="longitude"
								id="longitude"
								value={longitude}
								label={translations?.data.locations.longitude}
								errorText={longitude === '' ? validationErrors?.Longitude?.[0] : undefined}
								variant={'inverse'}
								onChange={modifyLongitude}
							/>
						</>
					)}

					<InputTextarea
						name="description"
						id="description"
						className={styles.description}
						label={translations?.data.locations.description}
						errorText={description === '' ? validationErrors?.Description?.[0] : undefined}
						value={description}
						onChange={modifyDescription}
					/>
				</fieldset>
			</form>

			{formId === 'edit-location-form' && type === LocationType.Site && (
				<Map
					latitude={latitude ? debouncedLatitude : undefined}
					longitude={longitude ? debouncedLongitude : undefined}
					apiKey={settings?.data.googleMapsApiKey || testKey}
					onDragEnd={updateMarkerByDragging}
					zoom={17}
				/>
			)}
		</>
	);
};
